import { useState, useRef, useCallback, useEffect } from 'react';
import { useGetOrder } from '@/api/order';
import { ChargeStates, PaymentStates, PollingStatus, type OrderResponse } from '@/types/order';
import { getApiErrorCode } from '@/utils/error';

interface OrderPollingState {
  status: PollingStatus;
  data?: OrderResponse;
  error?: string;
  errorCode?: string;
}

interface UseOrderPollingReturn {
  triggerOrderPolling: (orderId: string) => void;
  stop: () => void;
  statusOrderPolling: PollingStatus;
  data?: OrderResponse;
  error?: string;
  /**
   * Gateway `errorCode` from a thrown poll, kept alongside `error` because the
   * latter is flattened to `Error.message` (e.g. `"HTTP 401"`) and so cannot
   * tell an ended session from any other failure.
   */
  errorCode?: string;
}

const POLLING_INTERVAL = 2000;
const TERMINAL_STATES = new Set([ChargeStates.SUCCESS, ChargeStates.FAILED]);

const isTerminalState = (state: ChargeStates | PaymentStates): boolean => {
  return TERMINAL_STATES.has(state as ChargeStates);
};

const isSuccessState = (state: ChargeStates | PaymentStates): boolean => {
  return state === ChargeStates.SUCCESS;
};

const isFailedState = (state: ChargeStates | PaymentStates): boolean => {
  return state === ChargeStates.FAILED;
};

export const useOrderPolling = (): UseOrderPollingReturn => {
  const [state, setState] = useState<OrderPollingState>({
    status: PollingStatus.IDLE,
  });
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { mutateAsync: getOrder } = useGetOrder();

  const stop = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const poll = useCallback(
    async (orderId: string) => {
      try {
        const response = await getOrder(orderId);

        setState((prev) => ({
          ...prev,
          data: response,
        }));
        if (isTerminalState(response.state)) {
          stop();
          setState((prev) => ({
            ...prev,
            status: isSuccessState(response.state) ? PollingStatus.SUCCESS : PollingStatus.FAILED,
            error: isFailedState(response.state) ? response.publisherErrorMessage || 'Payment failed' : undefined,
          }));
        }
      } catch (error) {
        stop();
        const errorMessage = error instanceof Error ? error.message : 'An error occurred';
        setState({
          status: PollingStatus.FAILED,
          error: errorMessage,
          errorCode: getApiErrorCode(error),
        });
      }
    },
    [getOrder, stop]
  );

  const trigger = useCallback(
    (orderId: string) => {
      stop();

      setState({
        status: PollingStatus.POLLING,
      });

      poll(orderId);

      pollingIntervalRef.current = setInterval(() => {
        poll(orderId);
      }, POLLING_INTERVAL);
    },
    [poll, stop]
  );

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return {
    triggerOrderPolling: trigger,
    stop,
    statusOrderPolling: state.status,
    data: state.data,
    error: state.error,
    errorCode: state.errorCode,
  };
};
